import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { User, Database, Zap, Palette, FileText, BarChart } from "lucide-react";
import { useAdmin } from "./admin/useAdmin";
import { SELECT, OUTLINE_BTN } from "./admin/adminStyles";
import { AdminLogin } from "./admin/AdminLogin";
import { AdminMetricsTab } from "./admin/AdminMetricsTab";
import { AdminProfileTab } from "./admin/AdminProfileTab";
import { AdminAgentTab, AdminUiTab } from "./admin/AdminConfigTabs";
import { AdminDataTab, AdminScenariosTab } from "./admin/AdminDataTabs";

const TABS = [
  { value: "metrics", label: "Metrics", icon: BarChart },
  { value: "profile", label: "Profile", icon: User },
  { value: "data", label: "Data", icon: Database },
  { value: "agent", label: "Agent", icon: Zap },
  { value: "ui", label: "UI", icon: Palette },
  { value: "scenarios", label: "Scenarios", icon: FileText },
];

const AdminPanel = () => {
  const navigate = useNavigate();
  const admin = useAdmin();

  if (!admin.authenticated) return <AdminLogin onLogin={admin.login} />;

  return (
    <div className="min-h-screen bg-[#0a0e17]" data-testid="admin-panel">
      <header className="border-b border-gray-800 bg-[#0f1419] p-4">
        <div className="container mx-auto flex items-center justify-between gap-4 flex-wrap">
          <h1 className="text-2xl font-bold text-white">Admin Configuration Panel</h1>
          <div className="flex items-center gap-4 flex-wrap">
            <select value={admin.selectedUser || ""} onChange={(e) => admin.selectUser(e.target.value)} className={SELECT} data-testid="user-selector">
              {admin.users.map((user) => <option key={user.userId} value={user.userId}>{user.companyName || user.username || user.userId}</option>)}
            </select>
            <Button onClick={() => navigate(`/dashboard/${admin.selectedUser}`)} variant="outline" className="border-ism-teal text-ism-teal" data-testid="admin-view-dashboard-btn">View Dashboard</Button>
            <Button onClick={admin.logout} variant="outline" className={OUTLINE_BTN} data-testid="admin-logout-btn">Logout</Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto p-6">
        <Tabs defaultValue="metrics" className="space-y-6">
          <TabsList className="bg-[#0f1419] border border-gray-800 flex-wrap h-auto">
            {TABS.map(({ value, label, icon: Icon }) => (
              <TabsTrigger key={value} value={value} className="data-[state=active]:bg-ism-teal" data-testid={`admin-tab-${value}`}>
                <Icon className="h-4 w-4 mr-2" />{label}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="metrics">
            <AdminMetricsTab metrics={admin.platformMetrics} users={admin.users} onRefresh={admin.refresh} />
          </TabsContent>
          <TabsContent value="profile">
            <AdminProfileTab profile={admin.userProfile} setProfile={admin.setUserProfile} loading={admin.loading} onSave={admin.saveUserProfile} onReset={admin.loadUsers} />
          </TabsContent>
          <TabsContent value="data">
            <AdminDataTab loading={admin.loading} onGenerateAccounts={admin.generateSampleAccounts} onGeneratePipeline={admin.generateSamplePipeline} onClear={admin.clearAllData} />
          </TabsContent>
          <TabsContent value="agent">
            <AdminAgentTab loading={admin.loading} onTrigger={admin.triggerAgentUpdate} />
          </TabsContent>
          <TabsContent value="ui">
            <AdminUiTab />
          </TabsContent>
          <TabsContent value="scenarios">
            <AdminScenariosTab loading={admin.loading} onLoad={admin.loadScenario} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPanel;
